import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EM_CONTACTS } from '../../email/mockData';
import { useEmailAttachmentPicker, type EmailComposeAttachment } from '../../email/composeAttachments';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { EmailAttachmentList, EmailAttachmentPickerInput } from './EmailAttachmentPicker';

const composeEyebrow: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: 'var(--ink-4)',
};

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export interface EmailComposeSendPayload {
  to: string;
  subject: string;
  body: string;
  patientId?: number;
  attachments?: EmailComposeAttachment[];
}

export interface EmailComposeProps {
  onClose: () => void;
  minimized: boolean;
  onMinimize: () => void;
  onRestore: () => void;
  onSend?: (payload: EmailComposeSendPayload) => void;
  sending?: boolean;
}

function resolveComposeRecipient(
  recips: { email: string }[],
  input: string,
): string | null {
  if (recips.length === 1 && recips[0]?.email) return recips[0].email.trim();
  if (recips.length > 1) return recips[0]?.email.trim() ?? null;
  const typed = input.trim();
  return isValidEmail(typed) ? typed : null;
}

export function EmailCompose({
  onClose,
  minimized,
  onMinimize,
  onRestore,
  onSend,
  sending = false,
}: EmailComposeProps) {
  const [recips, setRecips] = useState<(typeof EM_CONTACTS)[number][]>([]);
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const inRef = useRef<HTMLInputElement>(null);
  const ddRef = useRef<HTMLDivElement>(null);
  const {
    inputRef: attachInputRef,
    attachments,
    openPicker,
    onFilesSelected,
    removeAttachment,
    clearAttachments,
  } = useEmailAttachmentPicker();

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (
        ddRef.current &&
        !ddRef.current.contains(e.target as Node) &&
        inRef.current &&
        !inRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const contacts = useMemo(() => {
    if (!input) return EM_CONTACTS;
    const q = input.toLowerCase();
    return EM_CONTACTS.filter((c) => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q));
  }, [input]);

  const add = (c: (typeof EM_CONTACTS)[number]) => {
    if (!recips.find((r) => r.id === c.id)) setRecips([...recips, c]);
    setInput('');
    setOpen(false);
  };

  const commitTypedEmail = () => {
    const email = input.trim();
    if (!isValidEmail(email)) return;
    if (recips.some((r) => r.email.toLowerCase() === email.toLowerCase())) {
      setInput('');
      return;
    }
    setRecips([
      ...recips,
      { id: `ext-${email}`, name: email, email, initials: email.slice(0, 2).toUpperCase() },
    ]);
    setInput('');
    setOpen(false);
  };

  const toEmail = resolveComposeRecipient(recips, input);
  const canSend =
    Boolean(toEmail && subject.trim() && body.trim() && onSend && !sending);

  const handleSend = () => {
    if (!toEmail || !subject.trim() || !body.trim() || !onSend || sending) return;
    onSend({
      to: toEmail,
      subject: subject.trim(),
      body: body.trim(),
      ...(attachments.length ? { attachments } : {}),
    });
    clearAttachments();
  };

  if (minimized) {
    return (
      <div className="em-compose-min" onClick={onRestore} role="button" tabIndex={0}>
        <Icon kind="mail" size={14} />
        <span style={{ fontSize: 13, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subject || 'New email'}</span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          style={{ background: 'transparent', border: 0, color: '#fff', cursor: 'pointer', display: 'inline-flex' }}
        >
          <Icon kind="x" size={15} />
        </button>
      </div>
    );
  }

  return (
    <div className="em-compose">
      <div
        style={{
          padding: '11px 15px',
          background: 'var(--sage-0)',
          color: '#fff',
          borderRadius: 'var(--r-lg) var(--r-lg) 0 0',
          display: 'flex',
          alignItems: 'center',
          gap: 9,
        }}
      >
        <Icon kind="mail" size={15} />
        <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>New email</span>
        <button type="button" onClick={onMinimize} title="Minimize" style={{ background: 'transparent', border: 0, color: '#fff', cursor: 'pointer', display: 'inline-flex', padding: 3 }}>
          <Icon kind="minus" size={15} />
        </button>
        <button type="button" onClick={onClose} title="Close" style={{ background: 'transparent', border: 0, color: '#fff', cursor: 'pointer', display: 'inline-flex', padding: 3 }}>
          <Icon kind="x" size={15} />
        </button>
      </div>

      <div style={{ position: 'relative', padding: '10px 15px', borderBottom: '1px solid var(--cream-2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink-3)', width: 50, flex: 'none' }}>To</span>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 5, minHeight: 28 }}>
            {recips.map((r) => (
              <span
                key={r.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '3px 6px 3px 10px',
                  borderRadius: 'var(--r-sm)',
                  background: 'var(--gold-tint)',
                  color: 'var(--gold)',
                  fontSize: 12.5,
                  fontWeight: 600,
                }}
              >
                {r.name}
                <button
                  type="button"
                  onClick={() => setRecips(recips.filter((x) => x.id !== r.id))}
                  style={{ border: 0, background: 'transparent', color: 'var(--gold)', cursor: 'pointer', display: 'inline-flex' }}
                >
                  <Icon kind="x" size={11} />
                </button>
              </span>
            ))}
            <input
              ref={inRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  commitTypedEmail();
                }
              }}
              onBlur={() => {
                if (isValidEmail(input)) commitTypedEmail();
              }}
              placeholder={recips.length ? '' : 'Search patients or type an email…'}
              style={{
                flex: 1,
                minWidth: 140,
                border: 0,
                outline: 'none',
                background: 'transparent',
                fontFamily: 'var(--font-body)',
                fontSize: 13.5,
                color: 'var(--ink-1)',
              }}
            />
          </div>
          <IconButton icon="book" size={28} title="Address book" onClick={() => setOpen(true)} />
        </div>
        {open ? (
          <div ref={ddRef} className="em-addr">
            <div style={{ ...composeEyebrow, padding: '6px 8px 8px' }}>Patients · {contacts.length}</div>
            {contacts.slice(0, 6).map((c) => (
              <div key={c.id} className="em-addr-row" onClick={() => add(c)} role="button" tabIndex={0}>
                <span
                  className="ms-av ms-av-patient"
                  style={{
                    width: 30,
                    height: 30,
                    fontSize: 11,
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 600,
                    fontFamily: 'var(--font-display)',
                  }}
                >
                  {c.initials}
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-0)' }}>{c.name}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-3)', marginTop: 1 }}>{c.email}</div>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div style={{ padding: '10px 15px', borderBottom: '1px solid var(--cream-2)', display: 'flex', alignItems: 'center', gap: 9 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink-3)', width: 50, flex: 'none' }}>Subject</span>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject"
          style={{
            flex: 1,
            border: 0,
            outline: 'none',
            background: 'transparent',
            fontFamily: 'var(--font-body)',
            fontSize: 13.5,
            fontWeight: 500,
            color: 'var(--ink-1)',
          }}
        />
      </div>

      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Write your message…"
        style={{
          flex: 1,
          minHeight: 200,
          padding: 15,
          border: 0,
          outline: 'none',
          background: 'transparent',
          resize: 'none',
          fontFamily: 'var(--font-body)',
          fontSize: 14,
          color: 'var(--ink-1)',
          lineHeight: 1.6,
          boxSizing: 'border-box',
        }}
      />

      <EmailAttachmentList attachments={attachments} onRemove={removeAttachment} />

      <div
        style={{
          padding: '11px 15px',
          borderTop: '1px solid var(--cream-2)',
          background: 'var(--cream-0)',
          display: 'flex',
          gap: 8,
          alignItems: 'center',
        }}
      >
        <Button
          variant="primary"
          size="sm"
          iconLeft="send"
          disabled={!canSend}
          loading={sending}
          onClick={handleSend}
        >
          Send
        </Button>
        <IconButton icon="attach" size={30} title="Attach" onClick={openPicker} />
        <IconButton icon="sparkle" size={30} title="AI draft" />
        <span style={{ flex: 1 }} />
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-4)' }}>From hello@unionsquare-dental.com</span>
      </div>
      <EmailAttachmentPickerInput inputRef={attachInputRef} onFilesSelected={onFilesSelected} />
    </div>
  );
}
